// Re-export helpers that consumers still need from planMatch
export type { Answers } from "./data/planMatch";

/** The wizard steps, in order. */
export type WizardStep =
  | "questionnaire"
  | "plan"
  | "payment"
  | "confirmation";

/** Step ordering for GO_BACK navigation. */
export const STEP_ORDER: WizardStep[] = [
  "questionnaire",
  "plan",
  "payment",
  "confirmation",
];

export type PaymentResult = {
  accountId: string;
  lastFour: string;
  chargedAmount: number;
  nextChargeDate: string;
};

export type AccountResult = {
  username: string;
  loginUrl: string;
  /** Internal test signup: NMI sandbox, nothing charged, invoice voided. */
  isTestSignup?: boolean;
};

// --- API response type for GET /products ---

/** A product from the SelfServeProducts table (questionnaire checkboxes). */
export type SelfServeProduct = {
  productKey: string;
  name: string;
  description: string;
  sortOrder: number;
};

// --- API response types for GET /plans and POST /build-plan ---

/** A single tier within a product's pricing ladder. */
export type PlanTier = {
  includedQty: number;
  price: number;
  overagePerUnit: number | null;
  isRecommended: boolean;
  isEnterprise: boolean;
  sortOrder: number;
};

/** A product definition returned by GET /plans. */
export type PlansProduct = {
  productKey: string;
  name: string;
  unit: string;
  includedLabel: string;
  overageLabel: string | null;
  billingPeriod: string;
  tiers: PlanTier[];
};

/** Response from GET /api/signup/plans. */
export type PlansResponse = {
  products: PlansProduct[];
  cardLimit: number;
  bundleRules: {
    reassignedDiscountPct: number;
    complianceGuideBundlePrice: number;
  };
};

/** A single line in a computed plan (POST /build-plan response). */
export type BuildPlanLine = {
  productKey: string;
  name: string;
  includedQty: number;
  coverage: string;
  price: number;
  period: "mo" | "yr";
  overageRate: number | null;
  overageUnit: string | null;
  features: string[];
  note: string | null;
};

/** Response from POST /api/signup/build-plan. */
export type BuildPlanResponse = {
  lines: BuildPlanLine[];
  monthlyTotal: number;
  annualTotal: number;
  overCardLimit: boolean;
};

export type WizardState = {
  step: WizardStep;
  answers: import("./data/planMatch").Answers;
  plan: BuildPlanResponse | null;
  toSales: boolean;
  payment: PaymentResult | null;
  account: AccountResult | null;
};

export type WizardAction =
  | { type: "SUBMIT_QUESTIONNAIRE"; answers: import("./data/planMatch").Answers; plan: BuildPlanResponse }
  | { type: "CONFIRM_PLAN" }
  | { type: "SUBMIT_PAYMENT"; payment: PaymentResult; account: AccountResult }
  | { type: "GO_TO_STEP"; step: WizardStep };

// --- API request/response types (DNC-342 contract) ---

export type PlanLineItem = {
  productKey: "dncscrub" | "reassigned" | "trustcall" | "complianceguide";
  tierVolume: number | null;
  computedPrice: number;
  period: "mo" | "yr";
};

export type BillingAddress = {
  street: string;
  city: string;
  state: string;
  zip: string;
};

export type CreditCard = {
  cardNumber: string;
  expirationMonth: string;
  expirationYear: string;
  cvv: string;
  cardholderName: string;
};

export type CreateAccountRequest = {
  plan: PlanLineItem[];
  billing: {
    firstName: string;
    lastName: string;
    email: string;
    company: string;
    phone: string;
    address: BillingAddress;
    /** Optional, customer-typed; Support verifies with the FTC. */
    ftcSan?: string;
    ftcOrgId?: string;
  };
  card: CreditCard;
  turnstileToken: string;
  username: string;
  password: string;
};

export type CreateAccountResponse = PaymentResult & AccountResult;

export type ApiError = {
  error: string;
  message: string;
};

/** Where "Book a Meeting" / "Talk to sales" links send the visitor. */
export const BOOK_MEETING_URL = "https://www.dnc.com/schedule-meeting/";

/** Prefill the HubSpot meeting form for both anchors and programmatic navigation. */
export function bookMeetingUrl(email?: string): string {
  return email ? `${BOOK_MEETING_URL}?email=${encodeURIComponent(email)}` : BOOK_MEETING_URL;
}

/** Support contact shown on the confirmation page; must match SignupEmails.cs. */
export const SUPPORT_EMAIL = "support@dnc.com";
export const SUPPORT_PHONE = "866.362.5478";
